import {
  type CodeBlockModel,
  CodeBlockSchema,
  ParagraphBlockModel,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import { matchModels } from '@blocksuite/affine-shared/utils';
import type { BlockComponent } from '@blocksuite/std';
import {
  InlineMarkdownExtension,
  type InlineMarkdownMatchContext,
} from '@blocksuite/std/inline';

import { canonicalizeCodeBlockLanguage } from './code-block-languages.js';

function getCodeBlockMarkdownTarget({
  inlineEditor,
  inlineRange,
  pattern,
  prefixText,
}: InlineMarkdownMatchContext<AffineTextAttributes>) {
  if (inlineEditor.yTextString.slice(0, inlineRange.index).includes('\n')) {
    return null;
  }

  const match = prefixText.match(pattern);
  if (!match || !inlineEditor.rootElement) return null;

  const blockComponent =
    inlineEditor.rootElement.closest<BlockComponent>('[data-block-id]');
  if (!blockComponent) return null;

  const { model, store } = blockComponent;
  if (
    matchModels(model, [ParagraphBlockModel]) &&
    model.props.type === 'quote'
  ) {
    return null;
  }

  const parent = store.getParent(model);
  if (!parent) return null;

  return {
    blockComponent,
    index: parent.children.indexOf(model),
    language: canonicalizeCodeBlockLanguage(match[1]),
    parent,
  };
}

export const CodeBlockMarkdownExtension =
  InlineMarkdownExtension<AffineTextAttributes>({
    name: 'code-block',
    completionHint: {
      type: 'code-block',
      isEligible: props => getCodeBlockMarkdownTarget(props) !== null,
    },
    pattern: /^```([a-zA-Z0-9]*)\s$/,
    action: props => {
      const target = getCodeBlockMarkdownTarget(props);
      if (!target) return;

      const { inlineEditor, inlineRange, prefixText } = props;
      const { blockComponent, index, language, parent } = target;
      const { model, std, store } = blockComponent;

      store.captureSync();
      const codeId = store.addBlock<CodeBlockModel>(
        CodeBlockSchema.model.flavour,
        { language },
        parent,
        index
      );

      if (model.text && model.text.length > prefixText.length) {
        const text = model.text.clone();
        store.addBlock('affine:paragraph', { text }, parent, index + 1);
        text.delete(0, prefixText.length);
      }
      store.deleteBlock(model, { bringChildrenTo: parent });

      focusTextModel(std, codeId);
    },
  });
